import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import TopBar from './TopBar';
import Sidebar from './Sidebar';
import BetaBanner from './BetaBanner';
import ViewAsBanner from './ViewAsBanner';
import WelcomeChecklist from '../Onboarding/WelcomeChecklist';
import TourProvider from '../../tours/TourProvider';
import OnboardingProvider from '../../onboarding/OnboardingProvider';
import FeedbackDialog from '../Feedback/FeedbackDialog';

const DRAWER_WIDTH = 284; // wide enough that Tim's full item names never truncate (UI review §4)
const DRAWER_COLLAPSED_WIDTH = 72;

function AppLayout() {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  // P6-01: the entry page (`/`) is Google-style — no sidebar, no ribbon; the header stays.
  const isEntry = useLocation().pathname === '/';

  const [sidebarOpen, setSidebarOpen] = React.useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 900) return false;
    const saved = localStorage.getItem('hugo-sidebar-open');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // On mobile, close sidebar when route changes or screen shrinks
  React.useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  const toggleSidebar = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    if (!isMobile) {
      localStorage.setItem('hugo-sidebar-open', JSON.stringify(newState));
    }
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const currentWidth = isMobile || isEntry ? 0 : (sidebarOpen ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH);

  return (
    <TourProvider>
    <OnboardingProvider>
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <TopBar
        onMenuClick={toggleSidebar}
        drawerWidth={currentWidth}
        isEntry={isEntry}
      />
      {!isEntry && (
        <Sidebar
          open={sidebarOpen}
          width={isMobile ? DRAWER_WIDTH : currentWidth}
          expandedWidth={DRAWER_WIDTH}
          isMobile={isMobile}
          onClose={closeSidebar}
        />
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          // No ml: the permanent Drawer already occupies its width as a flex
          // child — an extra margin double-offsets the content (visible gap).
          mt: { xs: '56px', sm: '64px' },
          pb: 6,
        }}
      >
        {!isEntry && <BetaBanner />}
        <ViewAsBanner />
        <WelcomeChecklist />
        <FeedbackDialog />
        {/* The floating "?" button was removed (UI review 2026-09-21 §3): it duplicated the
            Help & Docs pill and covered page controls; help lives in the header and both sidebars. */}
        <Box sx={{ p: isEntry ? 0 : { xs: 1.5, sm: 2, md: 3 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
    </OnboardingProvider>
    </TourProvider>
  );
}

export default AppLayout;
